/**
 * ImportLeadsModal — bulk-import a lead list (CSV) into one study.
 *
 * Recruiting teams keep call lists as spreadsheets (name + phone). This dialog
 * uploads such a CSV to POST /api/crm/studies/import-leads, which creates the
 * leads in the study, MERGES rows that match an existing patient (by phone or
 * email), and — deliberately — sends NO welcome text (recruiters text imported
 * leads by hand). The response tally is shown so the recruiter can see what
 * landed and review any flagged matches.
 *
 * Shared by the Leads-by-study view (per-study) and the StudyDetail header.
 */
import { useCallback, useRef, useState } from 'react';
import { AlertTriangle, CheckCircle2, Download, FileSpreadsheet, Loader2, Upload, X } from 'lucide-react';
import api from '../../services/api';
import { LeadImportSummary } from '../types';

interface ImportStudy {
  slug: string;
  title?: string | null;
  id?: string | null;
}

const TEMPLATE_CSV = 'name,phone,email\nJane Doe,(555) 123-4567,jane@example.com\nJohn Smith,555-987-6543,\n';

/** Trigger a client-side download of the CSV template. */
function downloadTemplate() {
  const blob = new Blob([TEMPLATE_CSV], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'lead-list-template.csv';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

const errMsg = (e: any): string =>
  e?.response?.data?.error || e?.response?.data?.detail || e?.message || 'Import failed';

export default function ImportLeadsModal({
  study,
  open,
  onClose,
  onImported,
}: {
  study: ImportStudy;
  open: boolean;
  onClose: () => void;
  onImported?: (summary: LeadImportSummary) => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<LeadImportSummary | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const reset = useCallback(() => {
    setFile(null);
    setError(null);
    setSummary(null);
    if (inputRef.current) inputRef.current.value = '';
  }, []);

  const close = useCallback(() => {
    if (uploading) return;
    reset();
    onClose();
  }, [uploading, reset, onClose]);

  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] || null;
    setError(null);
    setSummary(null);
    if (f && !/\.csv$/i.test(f.name)) {
      setFile(null);
      setError('Please choose a .csv file.');
      return;
    }
    setFile(f);
  };

  const submit = useCallback(async () => {
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('study_slug', study.slug);
      if (study.id) form.append('study_id', study.id);
      const res = await api.post('/api/crm/studies/import-leads', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const s: LeadImportSummary = res.data?.summary;
      setSummary(s);
      onImported?.(s);
    } catch (e: any) {
      setError(errMsg(e));
    } finally {
      setUploading(false);
    }
  }, [file, study.slug, study.id, onImported]);

  if (!open) return null;

  const studyLabel = study.title || study.slug;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 sm:items-center">
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Import leads"
        className="my-8 w-full max-w-lg rounded-xl border border-gray-200 bg-white shadow-xl dark:border-gray-700 dark:bg-gray-800"
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 border-b border-gray-100 p-4 dark:border-gray-700 sm:p-6">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-primary-50 p-2 dark:bg-primary-900/30">
              <Upload className="h-5 w-5 text-primary-600 dark:text-primary-400" />
            </div>
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Import leads</h2>
              <p className="truncate text-xs text-gray-500 dark:text-gray-400">
                into <span className="font-medium text-gray-700 dark:text-gray-200">{studyLabel}</span>
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={close}
            disabled={uploading}
            aria-label="Close"
            className="rounded-lg p-2 text-gray-400 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-40 dark:hover:bg-gray-700 dark:hover:text-gray-200"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="space-y-4 p-4 sm:p-6">
          {!summary ? (
            <>
              <div className="rounded-lg border border-gray-100 bg-gray-50 p-3 text-xs leading-relaxed text-gray-600 dark:border-gray-700 dark:bg-gray-900/30 dark:text-gray-300">
                <p>
                  Upload a <span className="font-medium">.csv</span> with a <span className="font-mono">name</span> and{' '}
                  <span className="font-mono">phone</span> column (<span className="font-mono">email</span> optional).
                  Leads already in the system are merged by phone or email.
                </p>
                <p className="mt-1.5 font-medium text-gray-700 dark:text-gray-200">
                  No welcome text is sent — recruiters send the first text manually.
                </p>
                <button
                  type="button"
                  onClick={downloadTemplate}
                  className="mt-2 inline-flex items-center gap-1.5 font-medium text-primary-700 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300"
                >
                  <Download className="h-3.5 w-3.5" /> Download CSV template
                </button>
              </div>

              {/* File picker */}
              <div>
                <input
                  ref={inputRef}
                  type="file"
                  accept=".csv,text/csv"
                  onChange={onPick}
                  className="hidden"
                  id="import-leads-file"
                />
                <label
                  htmlFor="import-leads-file"
                  className="flex min-h-[44px] cursor-pointer items-center justify-center gap-2 rounded-lg border-2 border-dashed border-gray-300 px-4 py-6 text-sm font-medium text-gray-600 transition-colors duration-150 hover:border-primary-400 hover:bg-primary-50/40 dark:border-gray-600 dark:text-gray-300 dark:hover:border-primary-500 dark:hover:bg-primary-900/10"
                >
                  <FileSpreadsheet className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                  {file ? file.name : 'Choose a CSV file'}
                </label>
              </div>

              {error && (
                <p className="flex items-center gap-1.5 text-sm text-red-600 dark:text-red-400">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0" /> {error}
                </p>
              )}
            </>
          ) : (
            <ImportSummaryView summary={summary} studyLabel={studyLabel} />
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 border-t border-gray-100 p-4 dark:border-gray-700 sm:p-6">
          {!summary ? (
            <>
              <button
                type="button"
                onClick={close}
                disabled={uploading}
                className="min-h-[44px] rounded-lg border border-gray-300 px-4 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 disabled:opacity-40 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submit}
                disabled={!file || uploading}
                className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-primary-600 px-4 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 disabled:opacity-50 sm:min-h-0"
              >
                {uploading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Importing…
                  </>
                ) : (
                  'Import'
                )}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={reset}
                className="min-h-[44px] rounded-lg border border-gray-300 px-4 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
              >
                Import another file
              </button>
              <button
                type="button"
                onClick={close}
                className="min-h-[44px] rounded-lg bg-primary-600 px-4 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 sm:min-h-0"
              >
                Done
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/** The post-import tally + first few row errors. */
function ImportSummaryView({ summary, studyLabel }: { summary: LeadImportSummary; studyLabel: string }) {
  const stat = (label: string, value: number, tone: string) => (
    <div className={`rounded-lg border px-3 py-2 ${tone}`}>
      <div className="text-lg font-semibold">{value.toLocaleString()}</div>
      <div className="text-xs">{label}</div>
    </div>
  );
  return (
    <div className="space-y-3">
      <p className="flex items-center gap-2 text-sm font-medium text-green-700 dark:text-green-400">
        <CheckCircle2 className="h-5 w-5" />
        Imported {summary.total.toLocaleString()} row{summary.total === 1 ? '' : 's'} into {studyLabel}
      </p>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        {stat('New leads', summary.created, 'border-primary-200 bg-primary-50 text-primary-800 dark:border-primary-900/50 dark:bg-primary-900/20 dark:text-primary-300')}
        {stat('Merged', summary.merged, 'border-gray-200 bg-gray-50 text-gray-700 dark:border-gray-700 dark:bg-gray-900/30 dark:text-gray-300')}
        {summary.flagged > 0 &&
          stat('Needs review', summary.flagged, 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900/50 dark:bg-amber-900/20 dark:text-amber-300')}
        {summary.duplicate_in_file > 0 &&
          stat('Dupes in file', summary.duplicate_in_file, 'border-gray-200 bg-gray-50 text-gray-600 dark:border-gray-700 dark:bg-gray-900/30 dark:text-gray-400')}
        {summary.invalid > 0 &&
          stat('Skipped (invalid)', summary.invalid, 'border-red-200 bg-red-50 text-red-700 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-300')}
      </div>

      {summary.flagged > 0 && (
        <p className="text-xs text-amber-700 dark:text-amber-400">
          {summary.flagged} lead{summary.flagged === 1 ? '' : 's'} matched an existing patient by one key but conflicted on
          another — review them under Merge candidates.
        </p>
      )}

      {summary.errors.length > 0 && (
        <div className="rounded-lg border border-red-100 bg-red-50/60 p-3 dark:border-red-900/40 dark:bg-red-900/10">
          <p className="mb-1 text-xs font-medium text-red-700 dark:text-red-400">
            Skipped rows{summary.invalid > summary.errors.length ? ` (showing ${summary.errors.length} of ${summary.invalid})` : ''}:
          </p>
          <ul className="max-h-40 space-y-0.5 overflow-y-auto text-xs text-red-700 dark:text-red-400">
            {summary.errors.map((e, i) => (
              <li key={i}>
                Row {e.row}: {e.error}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
